import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { creditNotesAPI, clientAPI, invoiceAPI } from '../lib/api';
import { formatINR, formatDate, todayISO, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import DatePicker from '../components/DatePicker';
import ClientPicker from '../components/ClientPicker';
import { toast } from 'sonner';
import { Plus, Search, Trash2, Loader2, FileMinus, Undo2, X } from 'lucide-react';

const REASONS = { correction: 'Correction', others: 'Others' };
// Radix Select can't hold '' as an item value, so "no invoice" gets a sentinel.
const NO_INVOICE = '__none__';

const STATUS_STYLES = {
  open: 'status-sent',
  refunded: 'status-accepted',
};

const EMPTY_LINE = () => ({ name: '', description: '', quantity: '1', rate: '' });
const EMPTY_FORM = () => ({
  client_id: '',
  issued_at: todayISO(),
  reason: 'correction',
  against_invoice_id: '',
  notes: '',
  lines: [EMPTY_LINE()],
});

const lineAmount = (l) => Math.round((parseFloat(l.quantity) || 0) * (parseFloat(l.rate) || 0) * 100) / 100;

export default function CreditNotes() {
  const { can } = useAuth();
  const canWrite = can('write');
  const canDelete = can('delete');

  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const fetchNotes = useCallback(async () => {
    try {
      const res = await creditNotesAPI.getAll();
      setNotes(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to fetch credit notes'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  // Clients + invoices are only needed by the "New credit note" dialog.
  useEffect(() => {
    if (!canWrite) return;
    Promise.all([clientAPI.getAll(), invoiceAPI.getAll()])
      .then(([cRes, iRes]) => {
        setClients(cRes.data);
        setInvoices(iRes.data);
      })
      .catch((err) => toast.error(apiError(err, 'Failed to load clients and invoices')));
  }, [canWrite]);

  const clientInvoices = useMemo(
    () => invoices.filter((i) => i.client_id === form.client_id && i.status !== 'draft'),
    [invoices, form.client_id],
  );

  const total = form.lines.reduce((s, l) => s + lineAmount(l), 0);

  const openNew = () => {
    setForm(EMPTY_FORM());
    setDialogOpen(true);
  };

  const updateLine = (i, field, value) =>
    setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, EMPTY_LINE()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) return toast.error('Pick a client');
    if (!form.issued_at) return toast.error('Pick a date');
    // Silently drop untouched rows; every remaining row must be complete.
    const lines = form.lines.filter((l) => l.name.trim() || l.rate !== '');
    if (lines.length === 0) return toast.error('Add at least one line item');
    if (lines.some((l) => !l.name.trim() || !(parseFloat(l.quantity) > 0) || !(parseFloat(l.rate) > 0))) {
      return toast.error('Each line needs a name, a quantity and a rate above zero');
    }
    setSaving(true);
    try {
      const body = {
        client_id: form.client_id,
        issued_at: form.issued_at,
        reason: form.reason,
        notes: form.notes,
        line_items: lines.map((l) => ({
          name: l.name.trim(),
          description: l.description.trim(),
          quantity: parseFloat(l.quantity),
          rate: parseFloat(l.rate),
        })),
      };
      if (form.against_invoice_id) body.against_invoice_id = form.against_invoice_id;
      const res = await creditNotesAPI.create(body);
      toast.success(res.data?.number ? `Credit note ${res.data.number} created` : 'Credit note created');
      setDialogOpen(false);
      fetchNotes();
    } catch (err) {
      toast.error(apiError(err, 'Failed to create credit note'));
    } finally {
      setSaving(false);
    }
  };

  const handleRefund = async (cn) => {
    if (!window.confirm(`Mark ${cn.number} as refunded? This records that ${formatINR(cn.total)} was paid back to ${cn.client_name}.`)) return;
    setBusyId(cn.id);
    try {
      await creditNotesAPI.refund(cn.id);
      toast.success('Marked as refunded');
      fetchNotes();
    } catch (err) {
      toast.error(apiError(err, 'Failed to mark as refunded'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (cn) => {
    if (!window.confirm(`Delete credit note ${cn.number}? This cannot be undone.`)) return;
    setBusyId(cn.id);
    try {
      await creditNotesAPI.delete(cn.id);
      toast.success('Credit note deleted');
      fetchNotes();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    } finally {
      setBusyId(null);
    }
  };

  const filtered = notes.filter((cn) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return [cn.number, cn.client_name, cn.against_invoice_number, REASONS[cn.reason], cn.notes]
      .join(' ')
      .toLowerCase()
      .includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Credit Notes</h1>
          <p className="text-gray-500 mt-1">Corrections and refunds issued to clients</p>
        </div>
        {canWrite && (
          <Button data-testid="add-credit-note-btn" className="bg-black hover:bg-black/90" onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" />
            New credit note
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="credit-note-search"
          placeholder="Search by number, client, invoice or notes"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="table-header text-left">Number</th>
                  <th className="table-header text-left hidden md:table-cell">Date</th>
                  <th className="table-header text-left">Client</th>
                  <th className="table-header text-left hidden md:table-cell">Against invoice</th>
                  <th className="table-header text-left hidden md:table-cell">Reason</th>
                  <th className="table-header text-right">Amount</th>
                  <th className="table-header text-center hidden md:table-cell">Status</th>
                  <th className="table-header text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((cn) => (
                    <tr key={cn.id} className="table-row" data-testid={`credit-note-row-${cn.id}`}>
                      <td className="table-cell font-mono text-xs font-medium whitespace-nowrap">{cn.number}</td>
                      <td className="table-cell text-gray-600 whitespace-nowrap hidden md:table-cell">{formatDate(cn.issued_at)}</td>
                      <td className="table-cell font-medium">
                        {cn.client_name}
                        <span className="md:hidden block text-xs font-normal text-gray-500">
                          {formatDate(cn.issued_at)}{cn.against_invoice_number ? ` · against ${cn.against_invoice_number}` : ''}
                        </span>
                        <span className={`md:hidden badge mt-1 ${STATUS_STYLES[cn.status] || 'badge-gray'}`}>{cn.status === 'refunded' ? 'Refunded' : 'Open'}</span>
                      </td>
                      <td className="table-cell font-mono text-xs hidden md:table-cell">{cn.against_invoice_number || '-'}</td>
                      <td className="table-cell hidden md:table-cell">{REASONS[cn.reason] || '-'}</td>
                      <td className="table-cell text-right font-mono whitespace-nowrap">{formatINR(cn.total)}</td>
                      <td className="table-cell text-center hidden md:table-cell">
                        <span className={`badge ${STATUS_STYLES[cn.status] || 'badge-gray'}`}>
                          {cn.status === 'refunded' ? 'Refunded' : 'Open'}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center justify-center gap-1">
                          {canWrite && cn.status === 'open' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`refund-credit-note-${cn.id}`}
                              onClick={() => handleRefund(cn)}
                              disabled={busyId === cn.id}
                              title="Mark refunded"
                            >
                              {busyId === cn.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-credit-note-${cn.id}`}
                              onClick={() => handleDelete(cn)}
                              disabled={busyId === cn.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-400">
                      <FileMinus className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{notes.length > 0 ? 'No credit notes match your search.' : 'No credit notes yet'}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* New credit note */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New credit note</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label>Client *</Label>
                <ClientPicker
                  clients={clients}
                  value={form.client_id}
                  onChange={(id) => setForm({ ...form, client_id: id, against_invoice_id: '' })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cn-date">Date *</Label>
                <DatePicker id="cn-date" value={form.issued_at} onChange={(v) => setForm({ ...form, issued_at: v })} />
              </div>
              <div className="space-y-2">
                <Label>Reason</Label>
                <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                  <SelectTrigger data-testid="credit-note-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(REASONS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Against invoice (optional)</Label>
                <Select
                  value={form.against_invoice_id || NO_INVOICE}
                  onValueChange={(v) => setForm({ ...form, against_invoice_id: v === NO_INVOICE ? '' : v })}
                  disabled={!form.client_id}
                >
                  <SelectTrigger data-testid="credit-note-invoice">
                    <SelectValue placeholder={form.client_id ? 'None' : 'Pick a client first'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_INVOICE}>None</SelectItem>
                    {clientInvoices.map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.invoice_number} · {formatINR(i.total)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line items */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Line items *</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine} data-testid="add-credit-note-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add line
                </Button>
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-3" data-testid={`credit-note-line-${i}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input
                      placeholder="Item name"
                      value={l.name}
                      onChange={(e) => updateLine(i, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Description (optional)"
                      value={l.description}
                      onChange={(e) => updateLine(i, 'description', e.target.value)}
                    />
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Quantity</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="font-mono"
                        value={l.quantity}
                        onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        aria-label={`Line ${i + 1} quantity`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Rate</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="font-mono"
                        value={l.rate}
                        onChange={(e) => updateLine(i, 'rate', e.target.value)}
                        aria-label={`Line ${i + 1} rate`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm">{formatINR(lineAmount(l))}</span>
                    {form.lines.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-700"
                        onClick={() => removeLine(i)}
                        title="Remove line"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center bg-black text-white rounded-md px-3 py-2">
                <span className="font-semibold">Total</span>
                <span className="font-mono font-semibold" data-testid="credit-note-total">{formatINR(total)}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cn-notes">Notes</Label>
              <Textarea
                id="cn-notes"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90" data-testid="save-credit-note-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create credit note'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
